import { useCallback, useEffect, useState, createRef } from 'react'
import { useEffectOnce, useLocalStorage } from 'usehooks-ts'
import styled from 'styled-components'
import Lottie from 'react-lottie'
import { Tooltip } from 'antd'

import { initializeApp } from 'firebase/app'
import { getDatabase, ref, onValue, runTransaction } from 'firebase/database'

import iconHeart from './images/icon-heart.svg'
import iconEncouraging from './images/icon-encouraging.svg'
import iconSad from './images/icon-sad.svg'

import animationHeart from './images/heart-animation.json'
import animationEncouraging from './images/encouraging-animation.json'
import animationSad from './images/sad-animation.json'

const firebaseConfig = {
  apiKey: 'AIzaSyDK2K4LgTvb2KcTE6bUMDElqGxYKCLlo-c',
  authDomain: 'emoji-reactions.europe-west1.firebasedatabase.app/',
  databaseURL: 'https://emoji-reactions.europe-west1.firebasedatabase.app/',
  storageBucket: 'ta-interaktiv.appspot.com',
  projectId: 'ta-interaktiv',
}

initializeApp(firebaseConfig)

const heartOptions = {
  loop: false,
  autoplay: true,
  animationData: animationHeart,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

const sadOptions = {
  loop: false,
  autoplay: true,
  animationData: animationSad,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

const encouragingOptions = {
  loop: false,
  autoplay: true,
  animationData: animationEncouraging,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

const EmojiReactions = ({
  tenant,
  colorMode,
  storyId,
  articleId,
  isDisplayOnly,
}: {
  tenant?: object
  colorMode?: string
  storyId: string
  articleId: string
  isDisplayOnly: boolean
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [heartVotes, setHeartVotes] = useState(0)
  const [heartClicked, setHeartClicked] = useState(false)
  const [playHeartAnimation, setPlayHeartAnimation] = useState(false)
  const [encouragingVotes, setEncouragingVotes] = useState(0)
  const [encouragingClicked, setEncouragingClicked] = useState(false)
  const [playEncouragingAnimation, setPlayEncouragingAnimation] =
    useState(false)
  const [sadVotes, setSadVotes] = useState(0)
  const [sadClicked, setSadClicked] = useState(false)
  const [playSadAnimation, setPlaySadAnimation] = useState(false)
  const [userReactions, setUserReactions] = useLocalStorage<{
    [x: string]: boolean
  }>(storyId, {})
  type Reactiondata = { [key: string]: boolean }

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 600)
  }

  useEffectOnce(() => {
    // set height when window is resized
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  useEffect(() => {
    getVotes()
  })

  const handleAddVote = (
    atrticleId: string,
    storyId: string,
    reactionId: string
  ) => {
    const db = getDatabase()

    const storyRef = ref(db, articleId + '/' + storyId + '/' + reactionId + '/')
    runTransaction(storyRef, (story) => {
      if (story) {
        story += 1
      } else {
        story = 1
      }
      return story
    })
  }

  const handleRemoveVote = (
    atrticleId: string,
    storyId: string,
    reactionId: string
  ) => {
    const db = getDatabase()
    const storyRef = ref(db, articleId + '/' + storyId + '/' + reactionId + '/')
    runTransaction(storyRef, (story) => {
      if (story) {
        story -= 1
      } else {
        story = 0
      }
      return story
    })
  }

  const getVotes = () => {
    const db = getDatabase()
    const storyRef = ref(db, articleId + '/' + storyId + '/')
    onValue(storyRef, (snapshot) => {
      const heartVotes = snapshot.child('heart').val()
      const sadVotes = snapshot.child('sad').val()
      const encouragingVotes = snapshot.child('encouraging').val()
      heartVotes && setHeartVotes(heartVotes)
      sadVotes && setSadVotes(sadVotes)
      encouragingVotes && setEncouragingVotes(encouragingVotes)
    })
    getLocalVotes()
  }

  const getLocalVotes = () => {
    if (userReactions !== null) {
      setHeartClicked(userReactions.heart)
      setSadClicked(userReactions.sad)
      setEncouragingClicked(userReactions.encouraging)
    }
  }

  const reactToStory = (reactionId: string, storyId: string) => {
    // console.log('reactToStory:', reactionId, storyId)
    // console.log('userReactions:', userReactions)
    if (Object.keys(userReactions).length > 0) {
      // console.log('add/remove reaction to existing object')

      const userReaction: Reactiondata = userReactions
      userReaction[reactionId] = !userReactions[reactionId]
      setUserReactions(userReaction)
      // console.log('userReactions', userReactions)

      if (userReactions[reactionId] === false) {
        // console.log('remove vote')
        handleRemoveVote(articleId, storyId, reactionId)
      } else {
        // console.log('add vote')
        handleAddVote(articleId, storyId, reactionId)

        if (reactionId === 'heart') {
          setPlayHeartAnimation(true)
        } else if (reactionId === 'sad') {
          setPlaySadAnimation(true)
        } else if (reactionId === 'encouraging') {
          setPlayEncouragingAnimation(true)
        }
      }
    } else {
      // console.log('create reaction object (no votes yet)')

      const userReaction: Reactiondata = userReactions
      userReaction[reactionId] = true
      setUserReactions(userReaction)
      // console.log('userReactions', userReactions)

      // console.log('add vote')
      handleAddVote(articleId, storyId, reactionId)
      if (reactionId === 'heart') {
        setPlayHeartAnimation(true)
      } else if (reactionId === 'sad') {
        setPlaySadAnimation(true)
      } else if (reactionId === 'encouraging') {
        setPlayEncouragingAnimation(true)
      }
    }
  }

  return (
    <Reactions isDisplayOnly={isDisplayOnly}>
      {!isDisplayOnly && (
        <>
          <div className='animation'>
            <Lottie
              isStopped={!playHeartAnimation}
              options={heartOptions}
              eventListeners={[
                {
                  eventName: 'complete',
                  callback: () =>
                    !playHeartAnimation ? setPlayHeartAnimation(false) : null,
                },
              ]}
              isClickToPauseDisabled={true}
            />
          </div>
          <div className='animation'>
            <Lottie
              isStopped={!playSadAnimation}
              options={sadOptions}
              eventListeners={[
                {
                  eventName: 'complete',
                  callback: () =>
                    !playSadAnimation ? setPlaySadAnimation(false) : null,
                },
              ]}
              isClickToPauseDisabled={true}
            />
          </div>
          <div className='animation'>
            <Lottie
              isStopped={!playEncouragingAnimation}
              options={encouragingOptions}
              eventListeners={[
                {
                  eventName: 'complete',
                  callback: () =>
                    !playEncouragingAnimation
                      ? setPlayEncouragingAnimation(false)
                      : null,
                },
              ]}
              isClickToPauseDisabled={true}
            />
          </div>
        </>
      )}
      <div
        className={
          isDisplayOnly ? 'label-wrapper display-only' : 'label-wrapper'
        }
      >
        <Tooltip
          title='Herzschmerz'
          overlayInnerStyle={{
            backgroundColor: colorMode === 'dark' ? '#222' : '#fff',
            color: colorMode === 'dark' ? '#fff' : '#202346',
          }}
          open={isDisplayOnly ? false : undefined}
        >
          <div
            className={
              heartClicked || isDisplayOnly
                ? 'label heart voted'
                : 'label heart'
            }
            onClick={() =>
              isDisplayOnly ? null : reactToStory('heart', storyId)
            }
          >
            <img
              className='icon'
              src={iconHeart}
              unselectable='on'
              alt='Herzschmerz'
            />
            <span className='count'>{heartVotes}</span>
          </div>
        </Tooltip>

        <Tooltip
          title='stark'
          overlayInnerStyle={{
            backgroundColor: colorMode === 'dark' ? '#222' : '#fff',
            color: colorMode === 'dark' ? '#fff' : '#202346',
          }}
          open={isDisplayOnly ? false : undefined}
        >
          <div
            className={
              encouragingClicked || isDisplayOnly
                ? 'label encouraging voted'
                : 'label encouraging'
            }
            onClick={() =>
              isDisplayOnly ? null : reactToStory('encouraging', storyId)
            }
          >
            <img className='icon' src={iconEncouraging} alt='stark' />
            <span className='count'>{encouragingVotes}</span>
          </div>
        </Tooltip>

        <Tooltip
          title='traurig'
          overlayInnerStyle={{
            backgroundColor: colorMode === 'dark' ? '#222' : '#fff',
            color: colorMode === 'dark' ? '#fff' : '#202346',
          }}
          open={isDisplayOnly ? false : undefined}
        >
          <div
            className={
              sadClicked || isDisplayOnly ? 'label sad voted' : 'label sad'
            }
            onClick={() =>
              isDisplayOnly ? null : reactToStory('sad', storyId)
            }
          >
            <img className='icon' src={iconSad} alt='traurig' />
            <span className='count'>{sadVotes}</span>
          </div>
        </Tooltip>
      </div>
    </Reactions>
  )
}

const Reactions = styled.div<{ isDisplayOnly: boolean }>`
  text-align: center;
  margin: 0 auto;
  position: ${(prop) => (prop.isDisplayOnly ? 'absolute' : 'relative')};
  right: 0;

  .label-wrapper {
    margin-top: 2em;
    z-index: 3;
    position: relative;
    .label {
      display: inline-block;
      cursor: pointer;
      background-color: var(--label-background);
      -webkit-box-shadow: 0px 2px 4px -4px rgba(0, 0, 0, 0.6);
      box-shadow: 0px 2px 4px -4px rgba(0, 0, 0, 0.6);
      min-width: 72px;
      margin-right: 0.4em;
      padding: 0.2em 0.6em;
      color: #7d7d7d;
      .icon {
        top: 3px;
        position: relative;
        margin-right: 0.4em;
        -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
        filter: grayscale(100%);
        -moz-user-select: none;
        -webkit-user-select: none;
        user-select: none;
      }
      .count {
        color: var(--text-color);
        font-size: 0.8em;
        letter-spacing: -0.05em;
        top: -2px;
        position: relative;
        -webkit-touch-callout: none; /* iOS Safari */
        -webkit-user-select: none; /* Safari */
        -khtml-user-select: none; /* Konqueror HTML */
        -moz-user-select: none; /* Old versions of Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
        user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
      }
    }
    .label:hover {
      .icon {
        @media screen and (min-width: 769px) {
          transform: scale(1.3);
          transition: all 0.2s ease-out;
          top: 2px;
          -webkit-filter: none;
          filter: none;
        }
      }
    }
    @media (hover: none) {
      .label:hover:not(.voted) {
        .icon {
          top: 3px;
          -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
          filter: grayscale(100%);
        }
      }
    }
    .label:hover,
    .label.voted {
      color: #202346;
      .icon {
        -webkit-filter: none;
        filter: none;
      }
    }
    .label.voted {
      font-weight: 600;
    }
  }
  .label-wrapper.display-only {
    z-index: unset;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 2px;
    .label {
      display: grid;
      grid-template-columns: 1fr 1fr;
      min-width: 20px;
      padding: 0;
      margin-right: 0.4em;
      font-weight: 400 !important;
      color: var(--gray-2);
      background-color: transparent;
      -webkit-box-shadow: none;
      box-shadow: none;
      .icon {
        transform: scale(1) !important;
        top: 3px !important;
        margin-right: 1px;
      }
    }
  }

  .animation {
    position: absolute;
    z-index: 3;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 0);
  }
`

export default EmojiReactions
